import React, { useEffect, useState } from 'react'
import { Container, Col, Row, ButtonToolbar } from 'react-bootstrap'
import { TimePicker } from 'antd'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import classNames from 'classnames'
import DatePickerInput from 'components/ui/forms/field/date-picker-input/DatePickerInput'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import TextAreaInput from 'components/ui/forms/field/text-area/TextArea'
import { BlockMeetingContent } from 'store/states/lessons.state'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/blocks.action'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import { MeetingTypes } from 'enum/meetingTypes.enum'
import { MeetingIntegrationTypes } from 'enum/meetingIntegrationTypes.enum'
import SelectUsers from 'components/ui/forms/field/select-users/SelectUsers'
import styles from './Meeting.module.scss'
import { FormValues, schema } from './constants'
import Panelists from './components/Panelists'

interface MeetingProps {
  onCancel(): void
}

const buildPanelist = () => ({
  name: '',
  email: '',
})

const Meeting: React.FC<MeetingProps> = ({ onCancel }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [disableFields, setDisableFields] = useState(false)
  const [disableHostPanetlist, setdisableHostPanetlist] = useState(false)
  const currentHour = moment().hour()
  const currentMinute = moment().minute()
  const nextHour = (currentHour + 1) % 24
  const startDate = useSelector(
    ({ courses }: IMainState) => courses.course.startDate
  )

  const defaultValuesHour = () => {
    const timeNow = new Date()
    const date = new Date(timeNow.getTime())
    date.setHours(date.getHours() + 1)
    if (timeNow.getHours() >= 23) {
      date.setHours(0)
      date.setDate(date.getDate() + 1)
    }
    return date
  }

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      title: '',
      description: '',
      panelists: [buildPanelist()],
      host: { name: '', email: '' },
      startDate: defaultValuesHour(),
    },
    reValidateMode: 'onChange',
  })

  const { register, handleSubmit, trigger, control, formState } = form
  const edit = useSelector(({ blocks }: IMainState) => blocks.edit)
  const blockMeeting: BlockMeetingContent = edit.blockInstanceable

  useEffect(() => {
    if (blockMeeting) {
      const { title, description, startTime, config, duration } = blockMeeting
      const timeNow = new Date()
      form.setValue('title', title)
      form.setValue('description', description)
      form.setValue('duration', duration)
      if (startTime) form.setValue('startDate', new Date(startTime))
      if (config) {
        form.setValue('panelists', config.panelists || [buildPanelist()])
        form.setValue('host', config.host || buildPanelist())
      }
      if (startTime) {
        const date = new Date(startTime)
        const dateMinus30 = new Date(date.getTime() - 30 * 60 * 1000)
        if (timeNow.getTime() >= date.getTime()) {
          setdisableHostPanetlist(true)
        }
        if (timeNow >= dateMinus30) {
          setDisableFields(true)
        }
      }
    }
  }, [blockMeeting, form])

  const disabledHoursCreate = () => {
    let disabledHoursArray: number[] = []
    const currentDate = new Date()
    const dateSelected = form.getValues('startDate')

    if (currentDate.getDay() !== dateSelected.getDay()) {
      disabledHoursArray = []
      return disabledHoursArray
    }

    if (nextHour <= 23) {
      disabledHoursArray = Array.from({ length: nextHour }, (_, i) => i)
    } else {
      disabledHoursArray = []
    }
    return disabledHoursArray
  }

  const disabledMinutesCreate = () => {
    let disabledMinutesArray: number[] = []
    const dateSelected = form.getValues('startDate')

    if (dateSelected.getHours() === nextHour) {
      disabledMinutesArray = Array.from({ length: currentMinute }, (_, i) => i)
    } else {
      disabledMinutesArray = []
    }
    return disabledMinutesArray
  }

  const handleOnSave = (values: FormValues) => {
    const { id, lessonId } = edit
    if (!id || !lessonId) return
    const { panelists, host, startDate, description, title, duration } = values
    dispatch(
      fromActions.Actions.editMeetingBlock({
        id,
        lessonId,
        config: {
          host,
          panelists: panelists?.filter((p) => p.name && p.email) || [],
        },
        description,
        title,
        duration,
        typeMeet: MeetingTypes.webinar,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        startTime: startDate?.toISOString() || null,
        integrationType: MeetingIntegrationTypes.zoom,
      })
    )
    onCancel()
  }

  return (
    <Container fluid>
      <Row noGutters>
        <Col xs={12}>
          <h1 className={styles.title}>
            {t(`administration.blocks.type.meeting.title`)}
          </h1>
        </Col>
        <Col xs={12} className={'p-0 pt-3'}>
          <TextInput
            {...register('title')}
            label={t('administration.blocks.type.meeting.fields.title')}
            placeHolder={''}
            disabled={false}
            isInvalid={!!form.formState.errors.title}
          />
        </Col>
        <Col xs={12} className={'p-0 pt-3'}>
          <Controller
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <TextAreaInput
                value={field.value}
                name={field.name}
                rows={3}
                label={t(
                  'administration.blocks.type.meeting.fields.description'
                )}
                isInvalid={fieldState.invalid}
                onChange={field.onChange}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={5}>
          <Controller
            control={form.control}
            name="startDate"
            render={({ field, fieldState }) => (
              <DatePickerInput
                name={field.name}
                label={t('administration.courses.new.form.dates.inital')}
                value={field.value}
                onChange={field.onChange}
                popperClassName={styles.datePicker}
                minDate={startDate ? new Date(startDate) : new Date()}
                isInvalid={fieldState.invalid}
                disabled={disableFields}
              />
            )}
          />
        </Col>
        <Col xs={12} lg={4}>
          <InputLabel
            label={t('administration.blocks.type.meeting.fields.hour')}
          />
          <Controller
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <TimePicker
                format="HH:mm"
                className={classNames('w-100', styles.timePicker)}
                value={moment(field.value)}
                onChange={(time) => {
                  form.setValue('startDate', time.toDate())
                }}
                allowClear={false}
                placeholder="HH:mm"
                disabledHours={disabledHoursCreate}
                disabledMinutes={disabledMinutesCreate}
                disabled={disableFields}
              />
            )}
          />
        </Col>
        <Col xs={12} lg={3}>
          <TextInput
            {...register('duration')}
            type="number"
            label={t('administration.blocks.type.meeting.fields.duration')}
            placeHolder={''}
            disabled={disableFields}
            isInvalid={!!form.formState.errors.duration}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={12}>
          <h1 className={styles.title}>
            {t('administration.blocks.type.meeting.host.title')}
          </h1>
        </Col>
        <Col xs={12} className="mt-2">
          <Row>
            <Col xs={6}>
              <Controller
                control={form.control}
                name="selectUser"
                render={() => (
                  <SelectUsers
                    form={form}
                    blockMeeting={blockMeeting}
                    disable={disableHostPanetlist}
                  />
                )}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Panelists
        control={control}
        trigger={trigger}
        disable={disableHostPanetlist}
      />
      <ButtonToolbar className="float-right mt-1">
        <Button
          onClick={() => {
            onCancel()
          }}
          variant={ButtonVariant.Secondary}
          size={ButtonSizes.Small}
        >
          {t('administration.blocks.type.multimedia.buttons.cancel')}
        </Button>
        <Button
          size={ButtonSizes.Small}
          disabled={!formState.isValid}
          onClick={() => {
            handleSubmit(handleOnSave)()
          }}
        >
          {t('administration.blocks.type.multimedia.buttons.accept')}
        </Button>
      </ButtonToolbar>
    </Container>
  )
}

export default Meeting
